import type {
  CrmAddress,
  CrmContact,
  CrmRecordDetail,
  CrmRecordSummary,
  CrmRecordType,
  CrmRecordTypeFields,
} from "@/lib/api/crm/masters";
import type { CrmRules } from "@/lib/api/crm/rules";
import type {
  ApplyCrmRecordChangesRequest,
  CreateCrmRecordWithDetailsRequest,
  CrmRecordTypeFieldsRequest,
} from "@/lib/validation/crm";
import { formatPhoneForDisplay, parsePhoneNumberToE164Strict } from "@/lib/validation/phoneNumber";
import {
  addressTextFields,
  recordFormFields,
  recordTypeFields,
  createsCustomer,
  requiresClientId,
  type AddressFormRow,
  type ContactFormRow,
  type OnboardingSelection,
  type RecordFormFieldName,
  type RecordFormTypeFields,
  type RecordFormValues,
} from "./recordFormSchema";

/**
 * Form row index of each item in a request list, keyed by the list's path
 * ("contacts", "contacts.creates", ...). Used to trace server issue paths back to form rows.
 */
export type RowIndexMap = Record<string, number[]>;

type ContactCreate = CreateCrmRecordWithDetailsRequest["contacts"][number];
type ContactUpdate = ApplyCrmRecordChangesRequest["contacts"]["updates"][number];
type AddressCreate = CreateCrmRecordWithDetailsRequest["addresses"][number];
type AddressUpdate = ApplyCrmRecordChangesRequest["addresses"]["updates"][number];
type RecordRequest = ApplyCrmRecordChangesRequest["record"];

const blankToNull = (value: string): string | null => value.trim() || null;

function copyFormField<K extends RecordFormFieldName>(
  target: Partial<RecordFormTypeFields>,
  field: K,
  value: RecordFormTypeFields[K],
): void {
  target[field] = value;
}

function recordFieldToForm<K extends RecordFormFieldName>(
  target: Partial<RecordFormTypeFields>,
  field: K,
  record: CrmRecordTypeFields,
): void {
  target[field] = recordFormFields[field].fromRecord(record[field], record);
}

function formFieldToRequest<K extends RecordFormFieldName>(
  target: CrmRecordTypeFieldsRequest,
  field: K,
  fields: Partial<RecordFormTypeFields>,
): void {
  const value = fields[field] ?? recordFormFields[field].empty;
  target[field] = recordFormFields[field].toRequest(value);
}

/**
 * Type field values for a (new) record type: values of fields the type keeps are carried over,
 * fields it doesn't have are dropped, and new ones start empty.
 */
export function typeFieldsFor(
  rules: CrmRules,
  recordType: CrmRecordType | "",
  previous: Partial<RecordFormTypeFields>,
): Partial<RecordFormTypeFields> {
  const fields: Partial<RecordFormTypeFields> = {};
  for (const field of recordTypeFields(rules, recordType)) {
    copyFormField(fields, field, previous[field] ?? recordFormFields[field].empty);
  }
  return fields;
}

export function emptyContactRow(): ContactFormRow {
  return { id: null, kind: "phone", value: "", label: "", tag_ids: [] };
}

export function emptyAddressRow(): AddressFormRow {
  return {
    id: null,
    label: "",
    address_line_one: "",
    address_line_two: "",
    city: "",
    state: "",
    zipcode: "",
    county: "",
    country: "",
    tag_ids: [],
  };
}

export function emptyRecordFormValues(masterId: string): RecordFormValues {
  return {
    master_id: masterId,
    new_group: null,
    record_type: "",
    owner_id: "",
    client_id: "",
    onboarding: null,
    long_name: "",
    short_name: "",
    tag_ids: [],
    fields: {},
    contacts: [],
    addresses: [],
  };
}

function toContactRow(contact: CrmContact): ContactFormRow {
  return {
    id: contact.id,
    kind: contact.kind,
    value: contact.kind === "phone" ? formatPhoneForDisplay(contact.value) : contact.value,
    label: contact.label ?? "",
    tag_ids: contact.tags.map((tag) => tag.id),
  };
}

function toAddressRow(address: CrmAddress): AddressFormRow {
  return {
    id: address.id,
    label: address.label ?? "",
    address_line_one: address.address_line_one ?? "",
    address_line_two: address.address_line_two ?? "",
    city: address.city ?? "",
    state: address.state ?? "",
    zipcode: address.zipcode ?? "",
    county: address.county ?? "",
    country: address.country ?? "",
    tag_ids: address.tags.map((tag) => tag.id),
  };
}

/** Starting values for editing a stored record. */
export function toRecordFormValues(rules: CrmRules, record: CrmRecordDetail): RecordFormValues {
  const fields: Partial<RecordFormTypeFields> = {};
  for (const field of recordTypeFields(rules, record.record_type)) {
    recordFieldToForm(fields, field, record);
  }
  return {
    master_id: record.master_record_id,
    new_group: null,
    record_type: record.record_type,
    owner_id: record.owner_id ?? "",
    client_id: "",
    onboarding: null,
    long_name: record.long_name,
    short_name: record.short_name,
    tag_ids: record.tags.map((tag) => tag.id),
    fields,
    contacts: record.contacts.map(toContactRow),
    addresses: record.addresses.map(toAddressRow),
  };
}

/**
 * The record a new record should belong to: the preset record or the closest record above it
 * whose type may own `recordType`. "" when there is none or the type has no owner.
 */
export function presetOwnerFor(
  rules: CrmRules,
  recordType: CrmRecordType | "",
  presetRecordId: string | null,
  records: CrmRecordSummary[],
): string {
  const ownerTypes = recordType ? rules.record_types[recordType].owner_types : null;
  if (!ownerTypes || !presetRecordId) return "";
  const recordsById = new Map(records.map((record) => [record.id, record]));
  const visited = new Set<string>();
  for (let record = recordsById.get(presetRecordId); record && !visited.has(record.id); record = record.owner_id ? recordsById.get(record.owner_id) : undefined) {
    visited.add(record.id);
    if (ownerTypes.includes(record.record_type)) return record.id;
  }
  return "";
}

/**
 * The contact value as sent to the API: phones (shown in national format in the form) are always sent as
 * E.164. A phone that doesn't parse is sent as typed, so request validation flags it on its row.
 */
function contactRequestValue(row: ContactFormRow): string {
  const value = row.value.trim();
  return row.kind === "phone" ? parsePhoneNumberToE164Strict(value) ?? value : value;
}

function toContactCreate(row: ContactFormRow): ContactCreate {
  return { kind: row.kind, value: contactRequestValue(row), label: blankToNull(row.label), tag_ids: row.tag_ids };
}

function toAddressCreate(row: AddressFormRow): AddressCreate {
  return {
    label: blankToNull(row.label),
    address_line_one: blankToNull(row.address_line_one),
    address_line_two: blankToNull(row.address_line_two),
    city: blankToNull(row.city),
    state: blankToNull(row.state),
    zipcode: blankToNull(row.zipcode),
    county: blankToNull(row.county),
    country: blankToNull(row.country),
    tag_ids: row.tag_ids,
  };
}

/**
 * Base and type fields. A blank short name sends the name (the API requires one).
 * "Sync to risk" (skip_harvest_iq_sync false) sets the HarvestIQ profile to the record's name;
 * not syncing clears it.
 */
function toRecordRequest(
  rules: CrmRules,
  recordType: CrmRecordType,
  values: RecordFormValues,
  ownerId: string | null,
): RecordRequest {
  const typeFields: CrmRecordTypeFieldsRequest = {};
  for (const field of recordTypeFields(rules, recordType)) {
    formFieldToRequest(typeFields, field, values.fields);
  }
  const longName = values.long_name.trim();
  if (typeFields.skip_harvest_iq_sync !== undefined && rules.record_types[recordType].fields.includes("harvest_iq_profile")) {
    typeFields.harvest_iq_profile = typeFields.skip_harvest_iq_sync ? null : longName;
  }
  return {
    record_type: recordType,
    owner_id: ownerId,
    short_name: values.short_name.trim() || longName,
    long_name: longName,
    tag_ids: values.tag_ids,
    ...typeFields,
  };
}

/**
 * The picked onboarding instance's id, plus its onboarding fields the record type has.
 * Nothing (a null id) unless the type creates a customer.
 */
function toOnboardingRequest(
  rules: CrmRules,
  recordType: CrmRecordType,
  onboarding: OnboardingSelection | null,
): Pick<CrmRecordTypeFieldsRequest, "partner_names" | "shirt_sizes" | "original_acres" | "bol_sequence"> & {
  onboarding_instance_id: string | null;
} {
  if (!onboarding || !createsCustomer(rules, recordType)) return { onboarding_instance_id: null };
  const typeFields = rules.record_types[recordType].fields;
  return {
    onboarding_instance_id: onboarding.instance_id,
    ...(typeFields.includes("partner_names") ? { partner_names: onboarding.partner_names } : {}),
    ...(typeFields.includes("shirt_sizes") ? { shirt_sizes: onboarding.shirt_sizes } : {}),
    ...(typeFields.includes("original_acres") ? { original_acres: onboarding.original_acres } : {}),
    ...(typeFields.includes("bol_sequence") ? { bol_sequence: onboarding.bol_sequence } : {}),
  };
}

/**
 * POST /crm/records/create-with-details. Call with values that passed the form schema.
 * client_id is sent only for types that require one (cleared otherwise); the server creates the customer.
 * The onboarding instance is sent only for types that create a customer; the server acks it.
 */
export function toCreateRequest(
  rules: CrmRules,
  values: RecordFormValues,
): { request: CreateCrmRecordWithDetailsRequest; rowIndexMap: RowIndexMap } | null {
  if (!values.record_type) return null;
  return {
    request: {
      // A blank group short name sends the group name (the API requires one), as for the record.
      master: values.new_group
        ? {
          short_name: values.new_group.short_name.trim() || values.new_group.long_name.trim(),
          long_name: values.new_group.long_name.trim(),
        }
        : { id: values.master_id },
      record: {
        ...toRecordRequest(rules, values.record_type, values, values.owner_id || null),
        client_id: requiresClientId(rules, values.record_type) ? values.client_id.trim() : null,
        ...toOnboardingRequest(rules, values.record_type, values.onboarding),
      },
      contacts: values.contacts.map(toContactCreate),
      addresses: values.addresses.map(toAddressCreate),
    },
    rowIndexMap: {
      contacts: values.contacts.map((_, index) => index),
      addresses: values.addresses.map((_, index) => index),
    },
  };
}

const sortedIds = (ids: string[]): string[] => [...ids].sort();

function sameContactRow(a: ContactFormRow, b: ContactFormRow): boolean {
  return a.kind === b.kind
    && contactRequestValue(a) === contactRequestValue(b)
    && a.label.trim() === b.label.trim()
    && sortedIds(a.tag_ids).join() === sortedIds(b.tag_ids).join();
}

function sameAddressRow(a: AddressFormRow, b: AddressFormRow): boolean {
  return addressTextFields.every((field) => a[field].trim() === b[field].trim())
    && sortedIds(a.tag_ids).join() === sortedIds(b.tag_ids).join();
}

/**
 * Splits form rows into creates (no id), full-replacement updates (changed existing rows) and
 * deletes (existing rows no longer in the form). Unchanged rows aren't sent.
 */
function toChangeSet<Row extends { id: string | null }, Create, Update>(
  originalRows: Row[],
  rows: Row[],
  sameRow: (a: Row, b: Row) => boolean,
  toCreate: (row: Row) => Create,
  toUpdate: (row: Row, id: string) => Update,
): { changes: { creates: Create[]; updates: Update[]; delete_ids: string[] }; creates: number[]; updates: number[] } {
  const originalsById = new Map(originalRows.flatMap((row) => (row.id ? [[row.id, row] as const] : [])));
  const keptIds = new Set(rows.flatMap((row) => (row.id ? [row.id] : [])));
  const changes = { creates: [] as Create[], updates: [] as Update[], delete_ids: [] as string[] };
  const creates: number[] = [];
  const updates: number[] = [];
  rows.forEach((row, index) => {
    if (!row.id) {
      changes.creates.push(toCreate(row));
      creates.push(index);
      return;
    }
    const original = originalsById.get(row.id);
    if (!original || !sameRow(original, row)) {
      changes.updates.push(toUpdate(row, row.id));
      updates.push(index);
    }
  });
  changes.delete_ids = [...originalsById.keys()].filter((id) => !keptIds.has(id));
  return { changes, creates, updates };
}

/**
 * PUT /crm/records/apply-changes. `original` is the form's starting values for `record`;
 * record type and owner are always sent back as stored. client_id and customer_id aren't sent.
 */
export function toApplyChangesRequest(
  rules: CrmRules,
  record: CrmRecordDetail,
  original: RecordFormValues,
  values: RecordFormValues,
): { request: ApplyCrmRecordChangesRequest; rowIndexMap: RowIndexMap } {
  const contacts = toChangeSet<ContactFormRow, ContactCreate, ContactUpdate>(
    original.contacts,
    values.contacts,
    sameContactRow,
    toContactCreate,
    (row, id) => ({ id, ...toContactCreate(row) }),
  );
  const addresses = toChangeSet<AddressFormRow, AddressCreate, AddressUpdate>(
    original.addresses,
    values.addresses,
    sameAddressRow,
    toAddressCreate,
    (row, id) => ({ id, ...toAddressCreate(row) }),
  );
  return {
    request: {
      record_id: record.id,
      expected_revision: record.revision,
      record: toRecordRequest(rules, record.record_type, values, record.owner_id),
      contacts: contacts.changes,
      addresses: addresses.changes,
    },
    rowIndexMap: {
      "contacts.creates": contacts.creates,
      "contacts.updates": contacts.updates,
      "addresses.creates": addresses.creates,
      "addresses.updates": addresses.updates,
    },
  };
}

/** Whether saving `values` would change anything on the record the form was opened with. */
export function hasRecordChanges(
  rules: CrmRules,
  record: CrmRecordDetail,
  original: RecordFormValues,
  values: RecordFormValues,
): boolean {
  const { request } = toApplyChangesRequest(rules, record, original, values);
  const { request: unchanged } = toApplyChangesRequest(rules, record, original, original);
  const comparable = (recordRequest: RecordRequest) =>
    JSON.stringify({ ...recordRequest, tag_ids: sortedIds(recordRequest.tag_ids) });
  return comparable(request.record) !== comparable(unchanged.record)
    || [request.contacts, request.addresses].some((changes) =>
      changes.creates.length > 0 || changes.updates.length > 0 || changes.delete_ids.length > 0);
}
